import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { resolveGoogleLink, GOOGLE_HINT } from "@/components/admin/googleLink";

const EMPTY = { title: "", description: "", instruments: "", teachers: [], date: "", time: "", location: "", price: "", level: "", photo_url: "", published: true };
const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

function safeDate(dateStr) {
  try {
    return format(new Date(dateStr), "d MMMM yyyy", { locale: fr });
  } catch {
    return dateStr;
  }
}

export default function StagesManager() {
  const { refresh } = useSite();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ stages_organizing: false, stages_organizing_photo: "" });
  const [savingBanner, setSavingBanner] = useState(false);

  const load = () => api.get("/admin/stages").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api
      .get("/settings")
      .then((r) => setBanner({ stages_organizing: !!r.data.stages_organizing, stages_organizing_photo: r.data.stages_organizing_photo || "" }))
      .catch(() => {});
  }, []);

  const saveBanner = async () => {
    setSavingBanner(true);
    try {
      await api.put("/admin/settings", banner);
      refresh();
      toast.success("Bandeau « stage en cours d'organisation » mis à jour sur le site");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingBanner(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, teachers: form.teachers.filter((t) => (t.name || "").trim()) };
    try {
      if (editingId) {
        await api.put(`/admin/stages/${editingId}`, payload);
        toast.success("Stage mis à jour");
      } else {
        await api.post("/admin/stages", payload);
        toast.success("Stage publié sur le site");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({
      title: s.title,
      description: s.description || "",
      instruments: s.instruments || "",
      teachers: (s.teachers || []).map((t) => ({ name: t.name || "", bio: t.bio || "" })),
      date: s.date || "",
      time: s.time || "",
      location: s.location || "",
      price: s.price || "",
      level: s.level || "",
      photo_url: s.photo_url || "",
      published: s.published !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce stage ?")) return;
    await api.delete(`/admin/stages/${id}`).catch(() => {});
    toast.success("Stage supprimé");
    load();
  };

  const setTeacher = (i, key) => (e) => {
    const teachers = form.teachers.map((t, j) => (j === i ? { ...t, [key]: e.target.value } : t));
    setForm({ ...form, teachers });
  };

  return (
    <div data-testid="stages-manager" className="space-y-10">
      {/* Bandeau « prochain stage en cours d'organisation » */}
      <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Bandeau « prochain stage »</h2>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input
            data-testid="stage-banner-checkbox"
            type="checkbox"
            checked={banner.stages_organizing}
            onChange={(e) => setBanner({ ...banner, stages_organizing: e.target.checked })}
            className="h-4 w-4 accent-[#B83227]"
          />
          Afficher « Prochain stage en cours d'organisation » en haut de la page Stages
        </label>
        {banner.stages_organizing && (
          <div>
            <label className={labelClass}>Photo du bandeau</label>
            <input
              data-testid="stage-banner-photo-input"
              className={inputClass}
              placeholder="https://..."
              value={banner.stages_organizing_photo}
              onChange={(e) => setBanner({ ...banner, stages_organizing_photo: e.target.value })}
              onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setBanner((b) => ({ ...b, stages_organizing_photo: u })))}
            />
            <p className="mt-1 text-xs text-stone-500">{GOOGLE_HINT}</p>
            <div className="mt-2">
              <FileUploadButton testId="stage-banner-photo-upload" accept="image/*" label="Ou téléverser une photo" onUploaded={(url) => setBanner((b) => ({ ...b, stages_organizing_photo: url }))} />
            </div>
          </div>
        )}
        <button onClick={saveBanner} data-testid="stage-banner-save-button" disabled={savingBanner} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
          {savingBanner ? "Enregistrement..." : "Enregistrer le bandeau"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        {/* Formulaire stage */}
        <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">{editingId ? "Modifier le stage" : "Ajouter un stage"}</h2>
          <div>
            <label className={labelClass}>Nom du stage *</label>
            <input data-testid="stage-title-input" className={inputClass} placeholder="Ex : Stage de cabrette — week-end de Pentecôte" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div>
            <label className={labelClass}>Instruments enseignés</label>
            <input data-testid="stage-instruments-input" className={inputClass} placeholder="Ex : Cabrette, cornemuse du Centre, accordéon (séparés par des virgules)" value={form.instruments} onChange={(e) => setForm({ ...form, instruments: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date</label>
              <input data-testid="stage-date-input" type="date" className={inputClass} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Horaires</label>
              <input data-testid="stage-time-input" className={inputClass} placeholder="Ex : 10h-17h" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Lieu</label>
            <input data-testid="stage-location-input" className={inputClass} placeholder="Ex : Salle des fêtes, Le Portel" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Tarif</label>
              <input data-testid="stage-price-input" className={inputClass} placeholder="Ex : 60 € le week-end" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Niveau minimum</label>
              <input data-testid="stage-level-input" className={inputClass} placeholder="Ex : débutant accepté, 1 an de pratique" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Présentation du stage</label>
            <textarea data-testid="stage-description-input" rows="4" className={inputClass} placeholder="Programme, repas, hébergement, matériel à prévoir..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Intervenants</label>
            <div className="space-y-3">
              {form.teachers.map((t, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-stone-200 p-3">
                  <div className="flex items-center gap-2">
                    <input data-testid={`stage-teacher-name-${i}`} className={inputClass} placeholder="Nom de l'intervenant" value={t.name} onChange={setTeacher(i, "name")} />
                    <button type="button" data-testid={`stage-teacher-remove-${i}`} onClick={() => setForm({ ...form, teachers: form.teachers.filter((_, j) => j !== i) })} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Retirer cet intervenant">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <textarea data-testid={`stage-teacher-bio-${i}`} rows="2" className={inputClass} placeholder="Petite présentation (parcours, instrument, groupe...)" value={t.bio} onChange={setTeacher(i, "bio")} />
                </div>
              ))}
              <button type="button" data-testid="stage-teacher-add-button" onClick={() => setForm({ ...form, teachers: [...form.teachers, { name: "", bio: "" }] })} className="inline-flex items-center gap-1.5 rounded border border-stone-300 px-4 py-2 text-xs font-bold uppercase text-stone-600 hover:border-stone-500">
                <Plus className="h-3.5 w-3.5" /> Ajouter un intervenant
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Photo du stage (facultatif)</label>
            <input data-testid="stage-photo-input" className={inputClass} placeholder="https://..." value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, photo_url: u })))} />
            <p className="mt-1 text-xs text-stone-500">{GOOGLE_HINT}</p>
            <div className="mt-2">
              <FileUploadButton testId="stage-photo-upload" accept="image/*" label="Ou téléverser une photo" onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input data-testid="stage-published-checkbox" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[#B83227]" />
            Visible sur le site
          </label>
          <div className="flex gap-3">
            <button type="submit" data-testid="stage-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
              {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Publier"}
            </button>
            {editingId && (
              <button type="button" data-testid="stage-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
                Annuler
              </button>
            )}
          </div>
        </form>

        {/* Liste des stages */}
        <div className="space-y-4">
          <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Stages ({items.length})</h2>
          {items.length === 0 && <p className="text-sm text-stone-500">Aucun stage. Les stages passés disparaissent automatiquement du site public.</p>}
          {items.map((s) => (
            <div key={s.id} data-testid={`stage-item-${s.id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-[#1E1B18]">
                    {s.title} {s.published === false && <span className="ml-1 rounded bg-stone-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-stone-600">masqué</span>}
                  </p>
                  <p className="text-xs text-stone-500">
                    {[s.date && safeDate(s.date), s.time, s.location].filter(Boolean).join(" · ") || "Sans date"}
                    {s.price && ` · ${s.price}`}
                    {s.level && ` · Niveau : ${s.level}`}
                  </p>
                  {s.instruments && <p className="mt-1 text-xs text-stone-500">Instruments : {s.instruments}</p>}
                  {s.teachers?.length > 0 && <p className="mt-1 text-xs text-stone-500">Intervenants : {s.teachers.map((t) => t.name).join(", ")}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => edit(s)} data-testid={`stage-edit-${s.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(s.id)} data-testid={`stage-delete-${s.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
